import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../../shared/utils/api';
import QuestionCard from './QuestionCard';
import QuestionResult from './QuestionResult';
import SessionSummary from './SessionSummary';

function generateSessionId() {
  return 'ps-' + Date.now() + '-' + Math.random().toString(36).slice(2, 9);
}

export default function QuestionSession({ config, onExit }) {
  // config: { module, mode, topicId, count, sessionType }
  const sessionType = config.sessionType || 'practice';
  const isExam = sessionType === 'exam';
  const totalExamSecs = (config.count || 10) * 150;

  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const [countdown, setCountdown] = useState(totalExamSecs);
  // Flag state (question index → bool)
  const [flagged, setFlagged] = useState({});
  // Skip queue — indices of skipped questions
  const [skipQueue, setSkipQueue] = useState([]);

  const sessionId = useRef(generateSessionId());
  const questionStartTime = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams({ mode: config.mode, count: config.count || 10 });
    if (config.module && config.module !== 'both') params.set('module', config.module);
    if (config.topicIds && config.topicIds.length > 0) {
      params.set('topic_ids', config.topicIds.join(','));
    } else if (config.topicId) {
      params.set('topic_id', config.topicId);
    }

    api(`/api/questions/practice?${params}`)
      .then(data => {
        if (!data.questions || data.questions.length === 0) {
          setError('No questions available for these settings. Try a different mode or topic.');
          setLoading(false);
          return;
        }
        setQuestions(data.questions);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load questions. Please try again.');
        setLoading(false);
      });
  }, [config]);

  // Timer effect
  useEffect(() => {
    if (loading || done || submitted) return;
    questionStartTime.current = Date.now();
    if (!isExam) setElapsedSecs(0);

    timerRef.current = setInterval(() => {
      if (isExam) {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setDone(true);
            return 0;
          }
          return prev - 1;
        });
      } else {
        setElapsedSecs(Math.floor((Date.now() - questionStartTime.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [currentIdx, loading, done, submitted, isExam]);

  const handleSubmit = useCallback(async (selectedAnswer) => {
    clearInterval(timerRef.current);
    const responseTime = questionStartTime.current
      ? (Date.now() - questionStartTime.current) / 1000
      : null;
    const question = questions[currentIdx];

    setSubmitted(true);
    let newAnswer;
    try {
      const result = await api('/api/questions/practice/answer', {
        method: 'POST',
        body: JSON.stringify({
          question_id: question.id,
          selected_answer: selectedAnswer,
          response_time_seconds: responseTime ? Math.round(responseTime) : null,
          session_id: sessionId.current,
        }),
      });
      newAnswer = { ...result, response_time_seconds: responseTime };
    } catch {
      newAnswer = {
        correct: false,
        correct_answer: '?',
        explanation: 'Could not record answer.',
        distractor_misconceptions: null,
        selected_answer: selectedAnswer,
        response_time_seconds: responseTime,
      };
    }

    if (isExam) {
      // Exam mode: advance immediately without showing result
      setAnswers(prev => [...prev, newAnswer]);
      const nextIdx = currentIdx + 1;
      if (nextIdx >= questions.length) {
        setDone(true);
      } else {
        setCurrentIdx(nextIdx);
        setSubmitted(false);
      }
    } else {
      setAnswers(prev => [...prev, newAnswer]);
    }
  }, [currentIdx, questions, isExam]);

  const handleNext = useCallback(() => {
    // Check skip queue — if there are skipped questions and we're at the end of the original set
    if (currentIdx + 1 >= questions.length) {
      if (skipQueue.length > 0) {
        const nextSkipped = skipQueue[0];
        setSkipQueue(q => q.slice(1));
        setCurrentIdx(nextSkipped);
        setSubmitted(false);
      } else {
        setDone(true);
      }
    } else {
      setCurrentIdx(i => i + 1);
      setSubmitted(false);
    }
  }, [currentIdx, questions.length, skipQueue]);

  // Skip — only in practice mode
  const handleSkip = useCallback(() => {
    if (isExam) return;
    setSkipQueue(prev => [...prev, currentIdx]);
    if (currentIdx + 1 >= questions.length) {
      if (skipQueue.length > 0) {
        const nextSkipped = skipQueue[0];
        setSkipQueue(q => q.slice(1));
        setCurrentIdx(nextSkipped);
      } else {
        if (answers.length > 0) setDone(true);
        else onExit();
      }
    } else {
      setCurrentIdx(i => i + 1);
    }
  }, [currentIdx, questions.length, skipQueue, answers.length, isExam, onExit]);

  // Flag toggle
  const toggleFlag = useCallback(() => {
    setFlagged(prev => ({ ...prev, [currentIdx]: !prev[currentIdx] }));
  }, [currentIdx]);

  const handleEndEarly = () => {
    if (answers.length > 0) {
      setDone(true);
    } else {
      onExit();
    }
  };

  function fmtTimer(s) {
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${String(rem).padStart(2, '0')}`;
  }

  if (loading) return <div className="qp-empty">Loading questions</div>;
  if (error) return (
    <div className="qp-empty">
      <div className="qp-error-message">{error}</div>
      <button className="btn-secondary" onClick={onExit}>Back</button>
    </div>
  );

  if (done) {
    return (
      <SessionSummary
        questions={questions.slice(0, answers.length)}
        answers={answers}
        flagged={flagged}
        onStartAnother={onExit}
        onReviewIncorrect={() => onExit({ reviewIncorrect: true })}
      />
    );
  }

  const question = questions[currentIdx];
  const total = questions.length;
  const progressPct = ((currentIdx + (submitted ? 1 : 0)) / total) * 100;
  const timerClass = isExam
    ? (countdown < 60 ? 'qp-timer-slow' : '')
    : (elapsedSecs > 120 ? 'qp-timer-slow' : elapsedSecs < 30 ? 'qp-timer-fast' : '');
  const isFlagged = flagged[currentIdx];

  return (
    <div className="qp-session qp-session-wide">
      <div className="qp-session-header">
        <span className="qp-progress-text">Question {currentIdx + 1} of {total}</span>
        {isExam && <span className="qp-exam-badge">Exam mode</span>}
        {!submitted && (
          <button
            className={`qp-flag-btn ${isFlagged ? 'qp-flag-btn-active' : ''}`}
            onClick={toggleFlag}
            title={isFlagged ? 'Remove flag' : 'Flag this question'}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M2.5 1a.5.5 0 0 1 .5.5V2h8.5a.5.5 0 0 1 .354.854L9.207 5l2.647 2.646A.5.5 0 0 1 11.5 8.5H3v6.5a.5.5 0 0 1-1 0V1.5a.5.5 0 0 1 .5-.5z"/>
            </svg>
          </button>
        )}
        {!submitted && (
          <span className={`qp-timer ${timerClass}`}>
            {isExam ? fmtTimer(countdown) : fmtTimer(elapsedSecs)}
          </span>
        )}
        <button className="btn-ghost qp-end-btn" onClick={handleEndEarly}>
          End session
        </button>
        {!isExam && !submitted && (
          <button className="btn-ghost qp-skip-btn" onClick={handleSkip}>
            Skip
          </button>
        )}
      </div>
      <div className="qp-progress-bar">
        <div className="qp-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {submitted && answers[currentIdx] ? (
        <QuestionResult
          question={question}
          result={answers[currentIdx]}
          onNext={handleNext}
          isLast={currentIdx + 1 >= total && skipQueue.length === 0}
        />
      ) : (
        <QuestionCard
          question={question}
          onSubmit={handleSubmit}
          disabled={submitted}
        />
      )}
    </div>
  );
}
